import { HttpParams } from '@angular/common/http'
import { Component } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { DataMap } from '@grapecity/wijmo.grid'

import { BaseComponent, EmployeeInjuryReport, FeatureType, Grid, ListItem, ModalSize, VpJob, VpUser } from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { EmployeeInjuryReportComponent } from './employee-injury-report.component'

@Component({
    selector: 'app-employee-injury-reports',
    templateUrl: './employee-injury-reports.component.html',
    standalone: true,
    imports: [
        FormsModule,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        GridExportComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
    ],
})
export class EmployeeInjuryReportsComponent extends BaseComponent {
    public grid: Grid<EmployeeInjuryReport>

    jobMap!: DataMap
    employeeMap!: DataMap
    activeJobList: ListItem<string>[] = []
    activeEmployeeList: ListItem<number>[] = []
    allJobs: VpJob[] = []
    allEmployees: VpUser[] = []

    constructor() {
        super()
        this.grid = new Grid<EmployeeInjuryReport>(this.services)
            .setupModalEditing(
                this.services.data.employeeInjuryReports,
                'Employee Injury Report',
                EmployeeInjuryReportComponent,
                () => EmployeeInjuryReport.createNew(),
                this,
                ModalSize.extraLarge,
            )
            .wrapColumnHeaders()
            .filterBy(item => [
                item.job,
                item.jobDisplay,
                item.location,
                item.injuryDescription,
                item.department,
                item.jobTitle,
                item.workStatusDisplay,
                this.employeeMap?.getDisplayValue(item.injuredEmployeeHrRef),
            ])
        this.grid.canEdit = this.canEdit
        this.grid.canAdd = this.canEdit
        this.grid.canDelete = this.canEdit
        this.refresh()
    }

    public refresh() {
        this.cancelableRequest(
            '',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.vpJobs.queryAllByAction('GetAllJobs'),
                    this.services.data.get<VpUser[]>('api/VpUser/GetVistaUsers', new HttpParams().set('activeOnly', true)),
                    this.services.data.get<VpUser[]>('api/VpUser/GetVistaUsers', new HttpParams().set('activeOnly', false)),
                    this.services.data.employeeInjuryReports.queryAll(),
                )
                .subscribe({
                    next: result => {
                        this.initialLoadingComplete()

                        const allJobs = result[0] as VpJob[]
                        this.allJobs = allJobs
                        this.activeJobList = allJobs
                            .filter(job => job.jobStatus === 1)
                            .sort((a, b) => a.id.trim().localeCompare(b.id.trim(), undefined, { numeric: true }))
                            .map(job => new ListItem(job.id, `${job.id.trim()} ${job.name}`))
                        this.jobMap = new DataMap(
                            allJobs.map(j => ({ id: j.id, display: `${j.id.trim()} ${j.name}` })),
                            'id',
                            'display',
                        )

                        const activeUsers = result[1] as VpUser[]
                        this.allEmployees = result[2] as VpUser[]
                        this.activeEmployeeList = activeUsers
                            .slice()
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map(u => new ListItem(u.id, u.name))
                        this.employeeMap = new DataMap(this.allEmployees, 'id', 'name')

                        const items = result[3] as EmployeeInjuryReport[]
                        for (const item of items) {
                            item.jobDisplay = this.jobDisplayForItem(item.job)
                            item.workStatusDisplay = this.workStatusForItem(item)
                        }
                        this.grid.items = items
                    },
                }),
        )
    }

    jobDisplayForItem(jobId: string | null | undefined): string {
        if (!jobId) return ''
        const job = this.allJobs.find(j => j.id === jobId || j.id.trim() === jobId.trim())
        return job ? `${job.id.trim()} ${job.name}` : jobId
    }

    workStatusForItem(item: EmployeeInjuryReport): string {
        const parts: string[] = []
        if (item.fullDutyNoRestrictions) parts.push('Full Duty')
        if (item.modifiedLightDutyRequired) parts.push('Modified/Light Duty')
        if (item.modifiedDutyDocumentSigned) parts.push('Modified Duty Doc Signed')
        return parts.join(', ')
    }

    public get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.SafetyChecklistEdit])
    }
}
